/**
 * P2 Enterprise Analytics - Phase 2
 * 
 * Advanced Analytics Dashboard Component (Admin/Team Lead Only)
 * - Processor Quality Index
 * - Client Breakdown
 * - Product Breakdown
 * 
 * Each tab displays:
 * - coverage/exclusion stats
 * - metadata.scope_filter_applied (for Phase 1 analytics reuse)
 * - date range selector
 * - CSV export functionality
 * 
 * Note: Rankings/Leaderboards intentionally excluded until governance review
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, 
  Users, 
  Building, 
  Package,
  Calendar,
  RefreshCw,
  Download,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Shield,
  Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { QCSpecialistTab, TypingActivityTab, ErrorAnalyticsTab } from './QCAnalyticsExtraTabs';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Date range presets
const DATE_RANGES = [
  { id: '7d', label: 'Last 7 Days', days: 7 },
  { id: '30d', label: 'Last 30 Days', days: 30 },
  { id: '90d', label: 'Last 90 Days', days: 90 }
];

// Confidence badge colors
const CONFIDENCE_COLORS = {
  high: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  low: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' }
};

/**
 * CSV Export Utility
 */
const exportToCSV = (data, headers, filename) => {
  if (!data || data.length === 0) {
    toast.error('No data available to export');
    return;
  }

  const csvRows = [];
  csvRows.push(headers.map(h => h.label).join(','));
  
  data.forEach(row => {
    const values = headers.map(h => {
      const value = row[h.key];
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value ?? '';
    });
    csvRows.push(values.join(','));
  });

  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  // Jun 14, 2026 — FILES-TOAST-001: removed CSV export success toast.
};

/**
 * Coverage Stats Component
 */
const CoverageStats = ({ coverage, title }) => {
  if (!coverage) return null;
  
  const coveragePercent = coverage.total_qc_orders_in_range > 0 
    ? ((coverage.eligible_orders_used / coverage.total_qc_orders_in_range) * 100).toFixed(1)
    : 0;
  
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-slate-600" />
        <h4 className="font-medium text-slate-700">Data Coverage - {title}</h4>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-slate-500">Total Orders</p>
          <p className="font-semibold text-slate-800">{coverage.total_qc_orders_in_range?.toLocaleString() || 0}</p>
        </div>
        <div>
          <p className="text-slate-500">Eligible/Used</p>
          <p className="font-semibold text-slate-800">{coverage.eligible_orders_used?.toLocaleString() || 0}</p>
        </div>
        <div>
          <p className="text-slate-500">Excluded</p>
          <p className="font-semibold text-amber-600">{coverage.excluded_orders?.toLocaleString() || 0}</p>
        </div>
        <div>
          <p className="text-slate-500">Coverage Rate</p>
          <p className="font-semibold text-green-600">{coveragePercent}%</p>
        </div>
      </div>
      {coverage.exclusion_reasons && Object.keys(coverage.exclusion_reasons).length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <p className="text-xs text-slate-500 mb-1">Exclusion Reasons:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(coverage.exclusion_reasons).map(([reason, count]) => (
              count > 0 && (
                <span 
                  key={reason} 
                  className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded border border-amber-200"
                >
                  {reason.replace(/_/g, ' ')}: {count}
                </span>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Metadata Banner Component
 */
const MetadataBanner = ({ metadata }) => {
  if (!metadata) return null;
  
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-blue-700">
        <p><strong>Date Filter:</strong> {metadata.date_filter_basis || 'qc_submitted_at'}</p>
        {metadata.formula && <p><strong>Formula:</strong> {metadata.formula}</p>}
        {metadata.sla_threshold_hours && <p><strong>SLA Threshold:</strong> {metadata.sla_threshold_hours} hours</p>}
        <p className="text-blue-500 mt-1">Generated: {formatDateTimeLocal(metadata.generated_at)}</p>
      </div>
    </div>
  );
};

/**
 * Processor Quality Tab
 */
const ProcessorQualityTab = ({ data, loading, onExport }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data || !data.processors) {
    return (
      <div className="text-center py-12 text-slate-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <p>No processor data available</p>
      </div>
    );
  }

  const { processors, totals, coverage, metadata } = data;

  return (
    <div>
      <CoverageStats coverage={coverage} title="Processor Quality" />
      <MetadataBanner metadata={metadata} />
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-sm">Processors</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totals?.total_processors || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <BarChart3 className="w-4 h-4" />
            <span className="text-sm">Submissions</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totals?.total_submissions?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <XCircle className="w-4 h-4" />
            <span className="text-sm">Rejections</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{totals?.total_rejections?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Avg Quality Index</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{totals?.avg_quality_index || 0}%</p>
        </div>
      </div>

      {/* Confidence Legend */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
        <p className="text-xs text-slate-600 mb-2"><strong>Confidence Levels:</strong></p>
        <div className="flex gap-4 text-xs">
          {metadata?.confidence_thresholds && Object.entries(metadata.confidence_thresholds).map(([level, desc]) => (
            <span key={level} className={`px-2 py-1 rounded ${CONFIDENCE_COLORS[level]?.bg} ${CONFIDENCE_COLORS[level]?.text}`}>
              {level.charAt(0).toUpperCase() + level.slice(1)}: {desc}
            </span>
          ))}
        </div>
      </div>

      {/* Processor Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Processor Quality Index</h3>
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="tf-thead">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Processor</th>
                <th className="text-center p-3 font-medium text-slate-600">Submissions</th>
                <th className="text-center p-3 font-medium text-slate-600">Approved</th>
                <th className="text-center p-3 font-medium text-slate-600" title="Returned for correction (any severity)">Returned</th>
                <th className="text-center p-3 font-medium text-slate-600" title="Critical returns / completed">Critical</th>
                <th className="text-center p-3 font-medium text-slate-600" title="Major returns / completed">Major</th>
                <th className="text-center p-3 font-medium text-slate-600" title="Minor returns / completed">Minor</th>
                <th className="text-center p-3 font-medium text-slate-600">Quality Index</th>
                <th className="text-center p-3 font-medium text-slate-600">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {processors.map((processor, idx) => {
                const returned = processor.returned_count ?? processor.rejected_count ?? 0;
                const critRate = processor.critical_return_rate_pct ?? 0;
                const majRate = processor.major_return_rate_pct ?? 0;
                const minRate = processor.minor_return_rate_pct ?? 0;
                return (
                  <tr key={processor.processor_id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-800">{processor.processor_name}</td>
                    <td className="p-3 text-center text-slate-600">{processor.submitted_count}</td>
                    <td className="p-3 text-center text-green-600">{processor.approved_count}</td>
                    <td className="p-3 text-center text-rose-600">{returned}</td>
                    <td className="p-3 text-center text-red-700">{critRate}%</td>
                    <td className="p-3 text-center text-amber-600">{majRate}%</td>
                    <td className="p-3 text-center text-slate-500">{minRate}%</td>
                    <td className="p-3 text-center">
                      <span className={`font-semibold ${processor.quality_index >= 90 ? 'text-green-600' : processor.quality_index >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                        {processor.quality_index}%
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${CONFIDENCE_COLORS[processor.confidence]?.bg} ${CONFIDENCE_COLORS[processor.confidence]?.text} ${CONFIDENCE_COLORS[processor.confidence]?.border} border`}>
                        {processor.confidence}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {processors.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-slate-500">No processor data available for this period</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * Client Breakdown Tab
 */
const ClientBreakdownTab = ({ data, loading, onExport }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data || !data.clients) {
    return (
      <div className="text-center py-12 text-slate-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <p>No client data available</p>
      </div>
    );
  }

  const { clients, totals, coverage, metadata } = data;

  return (
    <div>
      <CoverageStats coverage={coverage} title="Client Breakdown" />
      <MetadataBanner metadata={metadata} />
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <Building className="w-4 h-4" />
            <span className="text-sm">Total Clients</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totals?.total_clients || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <BarChart3 className="w-4 h-4" />
            <span className="text-sm">Total QC Volume</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totals?.total_volume?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-sm">SLA Threshold</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{metadata?.sla_threshold_hours || 4}h</p>
        </div>
      </div>

      {/* Client Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Client QC Metrics</h3>
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="tf-thead">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Client</th>
                <th className="text-center p-3 font-medium text-slate-600">QC Volume</th>
                <th className="text-center p-3 font-medium text-slate-600">Approved</th>
                <th className="text-center p-3 font-medium text-slate-600" title="Returned for correction (incl. legacy rejects)">Returned</th>
                <th className="text-center p-3 font-medium text-slate-600">Return Rate</th>
                <th className="text-center p-3 font-medium text-slate-600">SLA Compliance</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client, idx) => (
                <tr key={client.client_code || idx} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-medium text-slate-800">
                    {client.client_code === 'UNKNOWN' ? (
                      <span className="text-slate-400 italic">Unknown</span>
                    ) : client.client_code}
                  </td>
                  <td className="p-3 text-center text-slate-600">{client.qc_volume}</td>
                  <td className="p-3 text-center text-green-600">{client.approved_count}</td>
                  <td className="p-3 text-center text-rose-600">{client.rejected_count}</td>
                  <td className="p-3 text-center text-slate-600">{client.rejection_rate_pct}%</td>
                  <td className="p-3 text-center">
                    <span className={`font-semibold ${client.sla_compliance?.compliance_percentage >= 90 ? 'text-green-600' : client.sla_compliance?.compliance_percentage >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                      {client.sla_compliance?.compliance_percentage || 0}%
                    </span>
                    <span className="text-xs text-slate-400 ml-1">
                      ({client.sla_compliance?.within_sla || 0}/{(client.sla_compliance?.within_sla || 0) + (client.sla_compliance?.outside_sla || 0)})
                    </span>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">No client data available for this period</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * Product Breakdown Tab
 */
const ProductBreakdownTab = ({ data, loading, onExport }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data || !data.products) {
    return (
      <div className="text-center py-12 text-slate-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <p>No product data available</p>
      </div>
    );
  }

  const { products, totals, coverage, metadata } = data;

  return (
    <div>
      <CoverageStats coverage={coverage} title="Product Breakdown" />
      <MetadataBanner metadata={metadata} />
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <Package className="w-4 h-4" />
            <span className="text-sm">Product Types</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totals?.total_products || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <BarChart3 className="w-4 h-4" />
            <span className="text-sm">Total QC Volume</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totals?.total_volume?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-sm">SLA Threshold</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{metadata?.sla_threshold_hours || 4}h</p>
        </div>
      </div>

      {/* Product Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Product QC Metrics</h3>
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="tf-thead">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Product</th>
                <th className="text-center p-3 font-medium text-slate-600">QC Volume</th>
                <th className="text-center p-3 font-medium text-slate-600">Approved</th>
                <th className="text-center p-3 font-medium text-slate-600" title="Returned for correction (incl. legacy rejects)">Returned</th>
                <th className="text-center p-3 font-medium text-slate-600">Return Rate</th>
                <th className="text-center p-3 font-medium text-slate-600">SLA Compliance</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, idx) => (
                <tr key={product.product_name || idx} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-medium text-slate-800">
                    {product.product_name === 'UNKNOWN' ? (
                      <span className="text-slate-400 italic">Unknown</span>
                    ) : (
                      <span className="capitalize">{product.product_name}</span>
                    )}
                  </td>
                  <td className="p-3 text-center text-slate-600">{product.qc_volume}</td>
                  <td className="p-3 text-center text-green-600">{product.approved_count}</td>
                  <td className="p-3 text-center text-rose-600">{product.rejected_count}</td>
                  <td className="p-3 text-center text-slate-600">{product.rejection_rate_pct}%</td>
                  <td className="p-3 text-center">
                    <span className={`font-semibold ${product.sla_compliance?.compliance_percentage >= 90 ? 'text-green-600' : product.sla_compliance?.compliance_percentage >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                      {product.sla_compliance?.compliance_percentage || 0}%
                    </span>
                    <span className="text-xs text-slate-400 ml-1">
                      ({product.sla_compliance?.within_sla || 0}/{(product.sla_compliance?.within_sla || 0) + (product.sla_compliance?.outside_sla || 0)})
                    </span>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">No product data available for this period</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * Main QC Advanced Analytics Component
 */
const QCAdvancedAnalytics = () => {
  const [activeTab, setActiveTab] = useState('processor');
  const [dateRange, setDateRange] = useState('30d');
  // QC-ANALYTICS-EXT-001: custom From/To range + processor tax/title filter
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [appliedCustom, setAppliedCustom] = useState(null);
  const [processorProduct, setProcessorProduct] = useState('all');
  const [loading, setLoading] = useState(true);

  const [processorData, setProcessorData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [qcSpecialistData, setQcSpecialistData] = useState(null);
  const [typingData, setTypingData] = useState(null);
  const [errorData, setErrorData] = useState(null);

  const getDateRange = useCallback(() => {
    if (dateRange === 'custom' && appliedCustom) {
      // T23:59:59 makes the To date inclusive
      return { start_date: appliedCustom.start, end_date: `${appliedCustom.end}T23:59:59` };
    }
    const preset = DATE_RANGES.find(r => r.id === dateRange);
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - (preset?.days || 30));

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  }, [dateRange, appliedCustom]);

  const fetchAnalytics = useCallback(async () => {
    if (dateRange === 'custom' && !appliedCustom) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const { start_date, end_date } = getDateRange();
    const qs = `start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`;
    const productQs = processorProduct !== 'all' ? `&product=${processorProduct}` : '';

    try {
      const [processorRes, clientRes, productRes, qcSpecRes, typingRes, errorRes] = await Promise.all([
        fetch(`${API}/qc/analytics/processor-quality?${qs}${productQs}`, { headers }),
        fetch(`${API}/qc/analytics/client-breakdown?${qs}`, { headers }),
        fetch(`${API}/qc/analytics/product-breakdown?${qs}`, { headers }),
        fetch(`${API}/qc/analytics/qc-specialist-activity?${qs}`, { headers }),
        fetch(`${API}/qc/analytics/typing-activity?${qs}`, { headers }),
        fetch(`${API}/qc/analytics/error-analytics?${qs}`, { headers }),
      ]);

      if (processorRes.ok) {
        setProcessorData(await processorRes.json());
      } else if (processorRes.status === 403) {
        toast.error('Access denied: Admin or Team Leader role required');
      }
      if (clientRes.ok) setClientData(await clientRes.json());
      if (productRes.ok) setProductData(await productRes.json());
      if (qcSpecRes.ok) setQcSpecialistData(await qcSpecRes.json());
      if (typingRes.ok) setTypingData(await typingRes.json());
      if (errorRes.ok) setErrorData(await errorRes.json());
    } catch (err) {
      console.error('Error fetching advanced analytics:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, dateRange, appliedCustom, processorProduct]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const applyCustomRange = () => {
    if (!customStart || !customEnd) {
      toast.error('Select both From and To dates.');
      return;
    }
    if (customStart > customEnd) {
      toast.error('From date must be on or before To date.');
      return;
    }
    setAppliedCustom({ start: customStart, end: customEnd });
  };

  // Export handlers — QC-CONSOLIDATE-001 (Feb 2026): rejected_count is kept
  // as a column key (== returned_count under the new model) so existing CSV
  // consumers keep parsing. Severity-split columns are appended.
  const handleExportProcessor = () => {
    if (!processorData?.processors) return;

    const data = processorData.processors.map(p => ({
      processor_name: p.processor_name,
      submitted_count: p.submitted_count,
      approved_count: p.approved_count,
      returned_count: p.returned_count ?? p.rejected_count,
      critical_returns: p.critical_returns ?? 0,
      major_returns: p.major_returns ?? 0,
      minor_returns: p.minor_returns ?? 0,
      pending_count: p.pending_count,
      return_rate_pct: p.return_rate_pct ?? p.rejection_rate_pct,
      critical_return_rate_pct: p.critical_return_rate_pct ?? 0,
      major_return_rate_pct: p.major_return_rate_pct ?? 0,
      minor_return_rate_pct: p.minor_return_rate_pct ?? 0,
      quality_index: p.quality_index,
      confidence: p.confidence
    }));

    const headers = [
      { key: 'processor_name', label: 'Processor Name' },
      { key: 'submitted_count', label: 'Submissions' },
      { key: 'approved_count', label: 'Approved' },
      { key: 'returned_count', label: 'Returned' },
      { key: 'critical_returns', label: 'Critical Returns' },
      { key: 'major_returns', label: 'Major Returns' },
      { key: 'minor_returns', label: 'Minor Returns' },
      { key: 'pending_count', label: 'Pending' },
      { key: 'return_rate_pct', label: 'Return Rate (%)' },
      { key: 'critical_return_rate_pct', label: 'Critical Rate (%)' },
      { key: 'major_return_rate_pct', label: 'Major Rate (%)' },
      { key: 'minor_return_rate_pct', label: 'Minor Rate (%)' },
      { key: 'quality_index', label: 'Quality Index' },
      { key: 'confidence', label: 'Confidence' }
    ];

    exportToCSV(data, headers, 'processor_quality');
  };

  const handleExportClient = () => {
    if (!clientData?.clients) return;

    const data = clientData.clients.map(c => ({
      client_code: c.client_code,
      qc_volume: c.qc_volume,
      approved_count: c.approved_count,
      returned_count: c.rejected_count,
      return_rate_pct: c.rejection_rate_pct,
      sla_compliance_pct: c.sla_compliance?.compliance_percentage || 0,
      within_sla: c.sla_compliance?.within_sla || 0,
      outside_sla: c.sla_compliance?.outside_sla || 0
    }));

    const headers = [
      { key: 'client_code', label: 'Client Code' },
      { key: 'qc_volume', label: 'QC Volume' },
      { key: 'approved_count', label: 'Approved' },
      { key: 'returned_count', label: 'Returned' },
      { key: 'return_rate_pct', label: 'Return Rate (%)' },
      { key: 'sla_compliance_pct', label: 'SLA Compliance (%)' },
      { key: 'within_sla', label: 'Within SLA' },
      { key: 'outside_sla', label: 'Outside SLA' }
    ];

    exportToCSV(data, headers, 'client_breakdown');
  };

  const handleExportProduct = () => {
    if (!productData?.products) return;

    const data = productData.products.map(p => ({
      product_name: p.product_name,
      qc_volume: p.qc_volume,
      approved_count: p.approved_count,
      returned_count: p.rejected_count,
      return_rate_pct: p.rejection_rate_pct,
      sla_compliance_pct: p.sla_compliance?.compliance_percentage || 0,
      within_sla: p.sla_compliance?.within_sla || 0,
      outside_sla: p.sla_compliance?.outside_sla || 0
    }));

    const headers = [
      { key: 'product_name', label: 'Product Name' },
      { key: 'qc_volume', label: 'QC Volume' },
      { key: 'approved_count', label: 'Approved' },
      { key: 'returned_count', label: 'Returned' },
      { key: 'return_rate_pct', label: 'Return Rate (%)' },
      { key: 'sla_compliance_pct', label: 'SLA Compliance (%)' },
      { key: 'within_sla', label: 'Within SLA' },
      { key: 'outside_sla', label: 'Outside SLA' }
    ];

    exportToCSV(data, headers, 'product_breakdown');
  };

  // QC-ANALYTICS-EXT-001 exports
  const handleExportQCSpecialists = () => {
    if (!qcSpecialistData?.specialists) return;
    const data = qcSpecialistData.specialists.map(s => ({
      reviewer_name: s.reviewer_name,
      total_reviews: s.total_reviews,
      approvals: s.approvals,
      returns: s.returns,
      critical_returns: s.critical_returns,
      major_returns: s.major_returns,
      minor_returns: s.minor_returns,
      tax_reviews: s.tax?.reviews ?? 0,
      tax_approvals: s.tax?.approvals ?? 0,
      tax_returns: s.tax?.returns ?? 0,
      title_reviews: s.title?.reviews ?? 0,
      title_approvals: s.title?.approvals ?? 0,
      title_returns: s.title?.returns ?? 0,
    }));
    const headers = [
      { key: 'reviewer_name', label: 'QC Specialist' },
      { key: 'total_reviews', label: 'Reviews' },
      { key: 'approvals', label: 'Approvals' },
      { key: 'returns', label: 'Returns' },
      { key: 'critical_returns', label: 'Critical' },
      { key: 'major_returns', label: 'Major' },
      { key: 'minor_returns', label: 'Minor' },
      { key: 'tax_reviews', label: 'Tax QC Reviews' },
      { key: 'tax_approvals', label: 'Tax Approvals' },
      { key: 'tax_returns', label: 'Tax Returns' },
      { key: 'title_reviews', label: 'Title QC Reviews' },
      { key: 'title_approvals', label: 'Title Approvals' },
      { key: 'title_returns', label: 'Title Returns' },
    ];
    exportToCSV(data, headers, 'qc_specialist_activity');
  };

  const handleExportTyping = () => {
    if (!typingData?.typists) return;
    const data = typingData.typists.map(t => ({
      typist_name: t.typist_name,
      assigned: t.assigned,
      completed: t.completed,
      returned_to_search: t.returned_to_search,
      returned_to_qc: t.returned_to_qc ?? 0,
      corrections: t.corrections,
    }));
    const headers = [
      { key: 'typist_name', label: 'Typing Processor' },
      { key: 'assigned', label: 'Claimed/Assigned' },
      { key: 'completed', label: 'Completed' },
      { key: 'returned_to_search', label: 'Returned to Search' },
      { key: 'returned_to_qc', label: 'Returned to QC' },
      { key: 'corrections', label: 'Checklist Corrections' },
    ];
    exportToCSV(data, headers, 'typing_activity');
  };

  const handleExportErrors = () => {
    if (!errorData?.categories) return;
    const data = errorData.categories.map(c => ({
      category: c.category_label,
      count: c.count,
      pct_of_total: c.pct_of_total,
      minor: c.minor,
      major: c.major,
      critical: c.critical,
      unspecified: c.unspecified,
      tax_count: c.tax_count,
      title_count: c.title_count,
      processors: (c.processors || []).map(x => `${x.name} (${x.count})`).join('; '),
      qc_specialists: (c.qc_specialists || []).map(x => `${x.name} (${x.count})`).join('; '),
      clients: (c.clients || []).map(x => `${x.name} (${x.count})`).join('; '),
    }));
    const headers = [
      { key: 'category', label: 'Error Category' },
      { key: 'count', label: 'Errors' },
      { key: 'pct_of_total', label: '% of Total' },
      { key: 'minor', label: 'Minor' },
      { key: 'major', label: 'Major' },
      { key: 'critical', label: 'Critical' },
      { key: 'unspecified', label: 'Unspecified' },
      { key: 'tax_count', label: 'Tax' },
      { key: 'title_count', label: 'Title Search' },
      { key: 'processors', label: 'Affected Processors' },
      { key: 'qc_specialists', label: 'QC Specialists' },
      { key: 'clients', label: 'Clients' },
    ];
    exportToCSV(data, headers, 'error_analytics');
  };

  const tabs = [
    { id: 'processor', label: 'Processor Quality', icon: Users },
    { id: 'qcspecialist', label: 'QC Specialists', icon: Shield },
    { id: 'typing', label: 'Typing', icon: Clock },
    { id: 'errors', label: 'Error Analytics', icon: AlertCircle },
    { id: 'client', label: 'Client Breakdown', icon: Building },
    { id: 'product', label: 'Product Breakdown', icon: Package }
  ];

  return (
    <div className="p-6 bg-slate-100 min-h-screen" data-testid="qc-advanced-analytics">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          Advanced QC Analytics
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Detailed analytics for QC workflow optimization (Admin/Team Lead access)
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
        {/* Date Range Selector */}
        <div className="flex flex-wrap items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-600 mr-2">Date Range:</span>
          <div className="flex gap-1">
            {DATE_RANGES.map(range => (
              <button
                key={range.id}
                onClick={() => setDateRange(range.id)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  dateRange === range.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`date-range-${range.id}`}
              >
                {range.label}
              </button>
            ))}
            <button
              onClick={() => setDateRange('custom')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                dateRange === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="date-range-custom"
            >
              Custom
            </button>
          </div>
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2 ml-1" data-testid="custom-date-range">
              <input
                type="date"
                value={customStart}
                onChange={e => setCustomStart(e.target.value)}
                max={customEnd || undefined}
                className="px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                data-testid="custom-date-from"
              />
              <span className="text-sm text-slate-400">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={e => setCustomEnd(e.target.value)}
                min={customStart || undefined}
                className="px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                data-testid="custom-date-to"
              />
              <Button
                size="sm"
                onClick={applyCustomRange}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white h-8"
                data-testid="custom-date-apply"
              >
                Apply
              </Button>
            </div>
          )}
        </div>

        {/* Processor product filter — Processor Quality tab only. Scopes WHICH
            orders feed the view; the Quality Index formula is untouched. */}
        {activeTab === 'processor' && (
          <div className="flex items-center gap-2" data-testid="processor-product-filter">
            <span className="text-sm text-slate-600">Workflow:</span>
            <div className="flex gap-1">
              {[['all', 'All'], ['tax', 'Tax'], ['title', 'Title Search']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setProcessorProduct(val)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    processorProduct === val
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`processor-product-${val}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <Button 
          variant="outline" 
          size="sm"
          onClick={fetchAnalytics}
          disabled={loading}
          data-testid="refresh-analytics"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border border-slate-200 rounded-lg mb-6">
        <div className="flex border-b border-slate-200">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                    : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'processor' && (
            <ProcessorQualityTab 
              data={processorData} 
              loading={loading} 
              onExport={handleExportProcessor}
            />
          )}
          {activeTab === 'qcspecialist' && (
            <QCSpecialistTab
              data={qcSpecialistData}
              loading={loading}
              onExport={handleExportQCSpecialists}
            />
          )}
          {activeTab === 'typing' && (
            <TypingActivityTab
              data={typingData}
              loading={loading}
              onExport={handleExportTyping}
            />
          )}
          {activeTab === 'errors' && (
            <ErrorAnalyticsTab
              data={errorData}
              loading={loading}
              onExport={handleExportErrors}
            />
          )}
          {activeTab === 'client' && (
            <ClientBreakdownTab 
              data={clientData} 
              loading={loading} 
              onExport={handleExportClient}
            />
          )}
          {activeTab === 'product' && (
            <ProductBreakdownTab 
              data={productData} 
              loading={loading} 
              onExport={handleExportProduct}
            />
          )}
        </div>
      </div>

      {/* Footer Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Note on Data Quality</p>
          <p className="text-amber-700 mt-1">
            Metrics are calculated from available QC data. Coverage statistics show how complete the data is. 
            Low confidence indicators suggest more data is needed before drawing conclusions.
            Rankings are intentionally disabled until sufficient data history is available.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QCAdvancedAnalytics;
